import { useState } from "react";
import { Button, cn } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import type { DriverStop } from "../lib/normalize";

/**
 * F14 Slice 3: ステータス巻き戻し確認画面.
 * completed → in_transit に戻す。回数制限なし (当日内/承認後も可)。
 * reason は任意 (最大 500 文字)。
 */
export function StatusRevertConfirm({
  stop,
  acting,
  error,
  onBack,
  onSubmit,
}: {
  stop: DriverStop;
  acting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: (reason: string | undefined) => void;
}) {
  const [reason, setReason] = useState("");
  const overLimit = reason.length > 500;

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="集荷中に戻す" sub={stop.name} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">対象の配送</div>
          <div className="mb-0.5 text-[length:var(--fs-md)] font-semibold">{stop.name}</div>
          <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">予定 {stop.time}</div>
        </div>

        <div className="mb-3 rounded-xl bg-[var(--st-assigned-bg)] p-3.5">
          <div className="text-[length:var(--fs-sm)] font-semibold text-[var(--st-assigned-ink)]">
            ⚠ ステータスを「配送中」に戻します
          </div>
          <ul className="mt-2 list-disc pl-5 text-[length:var(--fs-xs)] text-[var(--st-assigned-ink)]">
            <li>誤って配送完了を押してしまった場合の取消です</li>
            <li>actual_departure (配送完了時刻) はクリアされます</li>
            <li>管理者/事務員には申請履歴として表示されます</li>
          </ul>
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">理由 (任意)</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="例: 納品先誤認のためキャンセル"
            className="w-full resize-none rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-sunken)] px-3 py-2 text-[length:var(--fs-sm)]"
          />
          <div
            className={cn(
              "mt-1 text-right text-[length:var(--fs-xs)]",
              overLimit ? "text-[var(--danger)]" : "text-[var(--ink-3)]",
            )}
          >
            {reason.length} / 500
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}
      </div>

      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={acting}
          className="h-auto min-h-[var(--row-h)] px-4 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          キャンセル
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => onSubmit(reason.trim() || undefined)}
          disabled={acting || overLimit}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
            acting && "cursor-wait opacity-70",
          )}
        >
          {acting ? "送信中…" : "集荷中に戻す"}
        </Button>
      </BottomBar>
    </div>
  );
}
